import React, { useRef, Fragment } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import "./TechnologiesPage.css";

// icons (replace paths with your real imports)
import toolsIcon from "../../assets/services/services1.png";
import svIcon    from "../../assets/services/services2.png";
import vhdlIcon  from "../../assets/services/services3.png";
import uvmIcon   from "../../assets/services/services4.png";
import formalIcon from "../../assets/services/services5.png";
import agileIcon  from "../../assets/services/services6.png";
import cicdIcon   from "../../assets/services/services11.png";
import emulatorIcon from "../../assets/services/services12.png";

export const technologies = [
  {
    id: "tools-languages",
    icon: toolsIcon,
    title: "Tools & Languages",
    description: `We leverage the industry's most powerful HDLs and frameworks to
build robust, maintainable hardware: from RTL through verification
and formal analysis.`,
    path: "/technologies/tools-and-languages",
    subs: [
      {
        id: "systemverilog",
        label: "SystemVerilog",
        description: `Our engineers write synthesizable RTL and comprehensive testbenches
in SystemVerilog, unlocking advanced abstraction and verification
capabilities.`,
        path: "/technologies/tools-and-languages/systemverilog",
        icon: svIcon,
      },
      {
        id: "vhdl",
        label: "VHDL",
        description: `Supporting legacy and new designs alike, our VHDL expertise ensures
seamless integration of critical IP and FPGA prototyping workflows.`,
        path: "/technologies/tools-and-languages/vhdl",
        icon: vhdlIcon,
      },
      {
        id: "uvm",
        label: "UVM",
        description: `We implement Universal Verification Methodology to create
modular, reusable verification components across multiple projects
and toolchains.`,
        path: "/technologies/tools-and-languages/uvm",
        icon: uvmIcon,
      },
      {
        id: "formal",
        label: "Formal Verification Tools",
        description: `Complement your simulation with formal property checking to
mathematically prove critical design properties and find hidden
corner‑case bugs.`,
        path: "/technologies/tools-and-languages/formal-verification-tools",
        icon: formalIcon,
      },
    ],
  },
  {
    id: "methodologies",
    icon: agileIcon,
    title: "Methodologies",
    description: `We apply modern hardware development best‑practices—agile flows,
CI/CD pipelines and more—to accelerate your time‑to‑market while
maintaining quality.`,
    path: "/technologies/methodologies",
    subs: [
      {
        id: "agile-hardware",
        label: "Agile Hardware Development",
        description: `Iterate quickly with short sprints, continuous integration,
and rapid feedback loops—bringing software‑style agility to ASIC
and FPGA projects.`,
        path: "/technologies/methodologies/agile-hardware-development",
        icon: agileIcon,
      },
      {
        id: "ci-cd",
        label: "CI/CD for Hardware",
        description: `Automate your builds, regressions and gate‑level regressions
in a cloud‑ or on‑premise pipeline, ensuring every commit is
verified before it merges.`,
        path: "/technologies/methodologies/ci-cd",
        icon: cicdIcon,
      },
    ],
  },
  {
    id: "platforms",
    icon: emulatorIcon,
    title: "Platforms",
    description: `We harness emulation, FPGA prototypes and virtual platforms to
validate full‑system functionality early—before silicon.`,
    path: "/technologies/platforms",
    subs: [
      {
        id: "emulators",
        label: "Emulators",
        description: `Leverage hardware emulators for software bring‑up, performance
characterization and system‑level debug at near‑silicon speeds.`,
        path: "/technologies/platforms/emulators",
        icon: emulatorIcon,
      },
    ],
  },
];

export default function TechnologiesPage() {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-100px 0px -100px 0px" });
  const { scrollYProgress } = useScroll();

  // New: Map scroll to color
  const bannerBackground = useTransform(
    scrollYProgress,
    [0, 0.5],  // start and end scroll points (tweak this if needed)
    ["#0f1f3b", "#ffffff"] // from white to dark blue
  );

  const titleColor = useTransform(
    scrollYProgress,
    [0, 0.5], 
    ["#ffffff", "#0f1f3b"] // from white to black
  );

  return (
    <>
      {/* full‑width banner */}
      <motion.div className="technologies-banner-wrapper" style={{ backgroundColor: bannerBackground }}>
        <div className="technologies-banner">
          <div className="technologies-banner-overlay">
            <motion.h1 style={{ color: titleColor }}>
              Used Technologies
            </motion.h1>
          </div>
        </div>
      </motion.div>

      {/* main content */}
      <motion.div
        className="technologies-page"
        ref={ref}
        initial={{ opacity: 0 }}
        animate={{ opacity: inView ? 1 : 0 }}
        transition={{ duration: 0.6 }}
      >
        {technologies.map((tech, idx) => {
          const isRow = tech.subs.length < 3;
          return (
            <Fragment key={tech.id}>
              <div className={`technology-section${isRow ? " technology-row" : ""}`}>
                <Link to={tech.path} className="main-card">
                  <img src={tech.icon} alt="" className="main-card-icon" />
                  <h2 className="main-card-title">{tech.title}</h2>
                  <p className="card-desc">{tech.description}</p>
                </Link>
                <div className="sub-grid">
                  {tech.subs.map((sub) => (
                    <Link key={sub.id} to={sub.path} className="sub-card">
                      <img src={sub.icon} alt="" className="sub-card-icon" />
                      <div>
                        <span className="sub-card-label">{sub.label}</span>
                        <p className="sub-desc">{sub.description}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
              {idx === 0 && <hr className="technology-separator" />}
            </Fragment>
          );
        })}
      </motion.div>
    </>
  );
}